import React, { useState, useEffect } from 'react';
import Header from '../Components/Header';
import Footer from '../Components/Footer';
import { Link } from 'react-router-dom';
import {
  Zap, MessageSquare, Phone, Mail, CheckCircle, 
  ArrowRight, Gift, Clock, Users, Star,
  Sparkles, Target, BarChart, TrendingUp, X
} from 'lucide-react';
import TagManager from 'react-gtm-module';
import * as ChannelService from '@channel.io/channel-web-sdk-loader';


const FreeStart = () => {
  // 스크롤 감지 상태
  const [scrolled, setScrolled] = useState(false);
  
  // 간단 문의 폼 상태
  const [quickForm, setQuickForm] = useState({
    name: '',
    contact: '', // 이메일 또는 전화번호
    company: '',
    message: '',
    contactMethod: 'email',
    privacy: false
  });
  
  // 로딩 및 성공 메시지 상태
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  // 스크롤 이벤트 핸들러
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 폼 입력 핸들러
  const handleQuickFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setQuickForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
      // 연락 방법이 변경되면 contact 필드 초기화
      ...(name === 'contactMethod' ? { contact: '' } : {})
    }));
  };

  // 폼 제출 핸들러
  const handleQuickFormSubmit = async (e) => {
    e.preventDefault();
    
    // 유효성 검사
    const newErrors = {};
    if (!quickForm.name.trim()) newErrors.name = '이름을 입력해주세요.';
    if (!quickForm.contact.trim()) newErrors.contact = quickForm.contactMethod === 'email' ? '이메일을 입력해주세요.' : '전화번호를 입력해주세요.';
    if (!quickForm.privacy) newErrors.privacy = '개인정보처리방침에 동의해주세요.';
    
    // 이메일 형식 검사
    if (quickForm.contactMethod === 'email' && quickForm.contact && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(quickForm.contact)) {
      newErrors.contact = '유효한 이메일 주소를 입력해주세요.';
    }
    
    // 전화번호 형식 검사
    if (quickForm.contactMethod !== 'email' && quickForm.contact && !/^[\d\-\+\(\)]{8,20}$/.test(quickForm.contact)) {
      newErrors.contact = '유효한 전화번호를 입력해주세요.';
    }

    if (Object.keys(newErrors).length > 0) {
      console.log('Validation errors:', newErrors);
      return;
    }

    setIsLoading(true);
    try {
      // Google Sheets와 동일한 API 엔드포인트 사용
      const SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbzln4YMAbLsLxbpSvlEgN9D3er1am0YosIubXqnTV5UCzN4PWLYpg3CPO0MzQgB3kHS/exec';
      
      // ContactForm과 동일한 형식으로 데이터 구성
      const submitData = {
        companyName: quickForm.company || '',
        name: quickForm.name,
        position: '',
        email: quickForm.contactMethod === 'email' ? quickForm.contact : '',
        phone: quickForm.contactMethod !== 'email' ? quickForm.contact : '',
        companySize: '',
        industry: '',
        preferredContact: quickForm.contactMethod,
        preferredTime: '',
        inquiry: quickForm.message || '무료 체험 신청',
        howDidYouHear: 'free_start_page',
        formType: 'free_trial' // 무료 체험 신청임을 구분
      };

      console.log('Submitting data:', submitData);

      await fetch(SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        redirect: 'follow',
        body: JSON.stringify(submitData)
      });

      // 성공 처리
      setSuccessMessage({
        title: '무료 체험 신청이 완료되었습니다!',
        message: '24시간 내에 담당자가 연락드려 무료 체험을 도와드리겠습니다.'
      });
      
      // GTM 이벤트 전송 (ContactForm과 동일한 방식)
      window.dataLayer = window.dataLayer || [];
      TagManager.dataLayer({
        dataLayer: {
          event: 'form_submission_success',
          form_name: 'free_trial_form',
          user_email: quickForm.contactMethod === 'email' ? quickForm.contact : '',
          contact_method: quickForm.contactMethod,
          form_type: 'free_trial'
        }
      });

      // 폼 초기화
      setQuickForm({
        name: '',
        contact: '',
        company: '',
        message: '',
        contactMethod: 'email',
        privacy: false
      });
      
    } catch (error) {
      console.error('Submission error:', error);
      setSuccessMessage({
        title: '신청 접수 중 오류가 발생했습니다.',
        message: '잠시 후 다시 시도해주시거나 채팅으로 문의해주세요.',
        isError: true
      });
    } finally {
      setIsLoading(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // 성공 메시지 닫기
  const closeSuccessMessage = () => {
    setSuccessMessage(null);
  };

  const openChat = () => ChannelService.showMessenger();

  // 문의 폼으로 스크롤하는 함수
  const scrollToContactForm = () => {
    const contactForm = document.getElementById('contact-form-section');
    if (contactForm) {
      contactForm.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  // 무료 체험 혜택 데이터
  const freeTrialBenefits = [
    {
      icon: <Clock className="w-6 h-6" />,
      title: "30일 무료 체험",
      description: "모든 기능을 30일간 무료로 사용해보세요",
      color: "blue"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "전담 지원팀",
      description: "설정부터 운영까지 전문가가 직접 도와드립니다",
      color: "indigo"
    },
    {
      icon: <BarChart className="w-6 h-6" />,
      title: "실시간 분석",
      description: "즉시 데이터를 확인하고 인사이트를 얻어보세요",
      color: "violet"
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "맞춤 설정",
      description: "귀하의 비즈니스에 최적화된 테스트 환경 구성",
      color: "rose"
    }
  ];

  // 시작 단계
  const startSteps = [
    {
      step: "01",
      title: "연락하기",
      description: "문의 양식 작성 또는 채팅으로 연락주세요",
      icon: <MessageSquare className="w-5 h-5" />
    },
    {
      step: "02", 
      title: "온라인 상담",
      description: "화상회의를 통해 서비스 온보딩과 설정을 함께 진행합니다",
      icon: <Users className="w-5 h-5" />
    },
    {
      step: "03",
      title: "서비스 설정",
      description: "계정 생성부터 첫 번째 테스트까지 모든 과정을 도와드립니다",
      icon: <Zap className="w-5 h-5" />
    },
    {
      step: "04",
      title: "체험 시작",
      description: "30일간 모든 기능을 자유롭게 사용해보세요",
      icon: <Star className="w-5 h-5" />
    }
  ];

  // 자주 묻는 질문
  const faqs = [
    {
      question: "정말 완전 무료인가요?",
      answer: "네, 30일간 모든 프리미엄 기능을 제한 없이 무료로 사용하실 수 있습니다. 카드 등록도 필요 없습니다."
    },
    {
      question: "기술적 지식이 없어도 가능한가요?",
      answer: "물론입니다! 전담 지원팀이 설정부터 운영까지 모든 과정을 도와드리므로 기술 지식 없이도 쉽게 시작할 수 있습니다."
    },
    {
      question: "무료 체험 후에는 어떻게 되나요?",
      answer: "30일 후 서비스를 계속 이용하고 싶으시면 유료 플랜으로 전환하시면 됩니다. 원하지 않으시면 자동으로 종료됩니다."
    },
    {
      question: "어떤 지원을 받을 수 있나요?",
      answer: "이메일, 채팅을 통한 실시간 지원과 함께 필요시 화상 미팅을 통한 지원을 제공합니다."
    }
  ];

  return (
    <div className="bg-white overflow-hidden">
      {/* 성공 메시지 모달 */}
      {successMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full animate-fade-in">
            <div className={`p-4 ${successMessage.isError ? 'bg-red-500' : 'bg-green-500'} text-white flex justify-between items-center rounded-t-xl`}>
              <h3 className="text-lg font-bold">{successMessage.isError ? '오류 발생' : '성공!'}</h3>
              <button onClick={closeSuccessMessage} className="text-white hover:text-gray-200">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 text-center">
              <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${successMessage.isError ? 'bg-red-100 text-red-500' : 'bg-green-100 text-green-500'} mb-4`}>
                {successMessage.isError ? (
                  <X className="w-8 h-8" />
                ) : (
                  <CheckCircle className="w-8 h-8" />
                )}
              </div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">{successMessage.title}</h2>
              <p className="text-gray-600 mb-6">{successMessage.message}</p>
              <button
                onClick={closeSuccessMessage}
                className={`px-6 py-3 rounded-lg shadow-md transition duration-200 ${
                  successMessage.isError 
                    ? 'bg-red-600 text-white hover:bg-red-700' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {successMessage.isError ? '다시 시도하기' : '확인'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Header />

      {/* 히어로 섹션 */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        {/* 배경 그라데이션 */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 -z-10"></div>

        {/* 배경 장식 요소 */}
        <div className="absolute top-0 right-0 -z-10 opacity-20">
          <svg width="600" height="600" viewBox="0 0 600 600" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="300" cy="300" r="300" fill="url(#radialGradient)" />
            <defs>
              <radialGradient id="radialGradient" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(300 300) scale(300)">
                <stop stopColor="#8B5CF6" />
                <stop offset="1" stopColor="#3B82F6" stopOpacity="0" />
              </radialGradient>
            </defs>
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 text-blue-800 rounded-full text-sm font-medium mb-6">
              <Gift className="w-4 h-4 mr-2" />
              지금 시작하면 30일 무료 + 전문가 지원
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight text-gray-900 mb-6">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600">
                무료로 시작하는
              </span><br />
              데이터 기반 성장
            </h1>
            
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              SWITCHUP의 전환율 최적화 솔루션을 30일간 완전 무료로 체험해보세요.<br />
              전문가가 직접 설정부터 운영까지 모든 과정을 도와드립니다.
            </p>

            {/* CTA 버튼들 */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              <button 
                onClick={scrollToContactForm}
                className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 font-medium flex items-center justify-center"
              >
                <MessageSquare className="w-5 h-5 mr-2" />
                무료 체험 신청하기
              </button>
              
              <div className="flex items-center text-gray-500">
                <span className="text-sm">또는</span>
              </div>
              
              <button 
                onClick={openChat}
                className="w-full sm:w-auto px-8 py-4 bg-white text-blue-600 border border-blue-200 rounded-lg shadow-lg hover:shadow-xl hover:bg-blue-50 transition-all duration-300 font-medium flex items-center justify-center"
              >
                <MessageSquare className="w-5 h-5 mr-2" />
                채팅으로 문의하기
              </button>
            </div>

            {/* 신뢰도 지표 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-2xl mx-auto">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">30일</div>
                <div className="text-sm text-gray-600">완전 무료 체험</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-indigo-600">24시간</div>
                <div className="text-sm text-gray-600">내 연락 보장</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">1:1</div>
                <div className="text-sm text-gray-600">전문가 지원</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 무료 체험 혜택 섹션 */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              무료 체험으로 받을 수 있는 모든 혜택
            </h2>
            <p className="text-xl text-gray-600">
              단순한 무료 체험이 아닙니다. 성공적인 시작을 위한 완벽한 지원을 받으세요.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {freeTrialBenefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 p-6 border border-gray-100 group">
                <div className={`w-12 h-12 rounded-lg bg-${benefit.color}-100 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <div className={`text-${benefit.color}-600`}>
                    {benefit.icon}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 시작 프로세스 섹션 */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              무료 체험 시작하는 방법
            </h2>
            <p className="text-xl text-gray-600">
              간단한 4단계로 바로 시작할 수 있습니다
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {startSteps.map((step, index) => (
              <div key={index} className="relative group">
                {/* 연결선 (마지막 항목 제외) */}
                {index < startSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 left-full w-8 h-0.5 bg-blue-200 -translate-y-1/2 z-0"></div>
                )}

                <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 p-6 border border-gray-100 group-hover:-translate-y-1 motion-reduce:transform-none motion-reduce:transition-none relative z-10">
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center mx-auto mb-4">
                      <div className="text-blue-600">
                        {step.icon}
                      </div>
                    </div>
                    <div className="text-sm font-medium text-blue-600 mb-2">{step.step}</div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                    <p className="text-gray-600 text-sm">{step.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 연락 방법 선택 섹션 */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">어떤 방법으로 시작하시겠어요?</h2>
          <p className="text-xl mb-10 text-blue-100">
            편한 방법으로 연락주시면 전문가가 빠르게 도움을 드리겠습니다
          </p>

          <div className="grid md:grid-cols-2 gap-8 max-w-2xl mx-auto">
            {/* 문의 양식 */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20 hover:bg-white/20 transition-colors duration-300">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">무료로 시작하기</h3>
              <p className="text-blue-100 mb-6">
                상세한 정보를 미리 작성하여<br />더 정확한 상담을 받으세요
              </p>
              <button 
                onClick={scrollToContactForm}
                className="w-full px-6 py-3 bg-white text-blue-600 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 font-medium"
              >
                무료로 시작하기
              </button>
            </div>

            {/* 카카오톡 문의 */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20 hover:bg-white/20 transition-colors duration-300">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">채팅 문의</h3>
              <p className="text-blue-100 mb-6">
                실시간 채팅으로 궁금한 점을<br />바로바로 해결하세요
              </p>
              <button 
                onClick={openChat}
                className="w-full px-6 py-3 bg-white/10 text-white border border-white/30 rounded-lg shadow-lg hover:shadow-xl hover:bg-white/20 transition-all duration-300 font-medium"
              >
                채팅 문의하기
              </button>
            </div>
          </div>

          <div className="mt-12 text-center">
            <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm">
              <CheckCircle className="w-4 h-4 mr-2" />
              평균 1시간 내 연락 • 24시간 내 상담 완료 보장
            </div>
          </div>
        </div>
      </section>

      {/* 간단 문의 폼 섹션 */}
      <section id="contact-form-section" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              30초 만에 무료 체험 신청
            </h2>
            <p className="text-xl text-gray-600">
              간단한 정보만 남겨주시면 24시간 내에 연락드리겠습니다
            </p>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100">
            <form onSubmit={handleQuickFormSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* 이름 */}
                <div>
                  <label htmlFor="quick-name" className="block text-sm font-medium text-gray-700 mb-2">
                    이름 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="quick-name"
                    name="name"
                    value={quickForm.name}
                    onChange={handleQuickFormChange}
                    placeholder="이름을 입력해주세요"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                    required
                  />
                </div>

                {/* 회사명 */}
                <div>
                  <label htmlFor="quick-company" className="block text-sm font-medium text-gray-700 mb-2">
                    회사명
                  </label>
                  <input
                    type="text"
                    id="quick-company"
                    name="company"
                    value={quickForm.company}
                    onChange={handleQuickFormChange}
                    placeholder="회사명을 입력해주세요"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                  />
                </div>
              </div>

              {/* 선호하는 연락 방법 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  선호하는 연락 방법 <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { value: 'email', label: '이메일', icon: <Mail className="w-4 h-4" /> },
                    { value: 'phone', label: '전화', icon: <Phone className="w-4 h-4" /> },
                    { value: 'sms', label: '문자', icon: <MessageSquare className="w-4 h-4" /> },
                    { value: 'kakao', label: '카카오톡', icon: <MessageSquare className="w-4 h-4" /> }
                  ].map((method) => (
                    <div key={method.value}>
                      <input
                        type="radio"
                        id={`contact-${method.value}`}
                        name="contactMethod"
                        value={method.value}
                        checked={quickForm.contactMethod === method.value}
                        onChange={handleQuickFormChange}
                        className="sr-only"
                      />
                      <label
                        htmlFor={`contact-${method.value}`}
                        className={`flex items-center justify-center px-4 py-3 border rounded-lg cursor-pointer transition-all duration-200 ${
                          quickForm.contactMethod === method.value
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-300 bg-white text-gray-700 hover:border-blue-300 hover:bg-blue-50'
                        }`}
                      >
                        {method.icon}
                        <span className="ml-2 text-sm font-medium">{method.label}</span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* 연락처 (이메일 또는 전화번호) */}
              <div>
                {quickForm.contactMethod === 'email' ? (
                  <>
                    <label htmlFor="quick-contact" className="block text-sm font-medium text-gray-700 mb-2">
                      이메일 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="quick-contact"
                      name="contact"
                      value={quickForm.contact}
                      onChange={handleQuickFormChange}
                      placeholder="이메일을 입력해주세요"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                      required
                    />
                  </>
                ) : (
                  <>
                    <label htmlFor="quick-contact" className="block text-sm font-medium text-gray-700 mb-2">
                      전화번호 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="quick-contact"
                      name="contact"
                      value={quickForm.contact}
                      onChange={handleQuickFormChange}
                      placeholder="010-1234-5678"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                      required
                    />
                  </>
                )}
              </div>

              {/* 간단한 문의 */}
              <div>
                <label htmlFor="quick-message" className="block text-sm font-medium text-gray-700 mb-2">
                  간단한 문의 (선택)
                </label>
                <textarea
                  id="quick-message"
                  name="message"
                  value={quickForm.message}
                  onChange={handleQuickFormChange}
                  rows="3"
                  placeholder="궁금한 점이나 요청사항을 간단히 적어주세요"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors resize-none"
                ></textarea>
              </div>

              {/* 개인정보 동의 */}
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="quick-privacy"
                  name="privacy"
                  checked={quickForm.privacy}
                  onChange={handleQuickFormChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-1"
                  required
                />
                <label htmlFor="quick-privacy" className="ml-3 text-sm text-gray-600">
                  <Link to="/privacy" className="text-blue-600 hover:underline">개인정보처리방침</Link>에 동의합니다. <span className="text-red-500">*</span>
                </label>
              </div>

              {/* 제출 버튼 */}
              <div className="text-center pt-4">
                <button
                  type="submit"
                  disabled={!quickForm.name || !quickForm.contact || !quickForm.privacy || isLoading}
                  className="px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg shadow-lg hover:shadow-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 font-medium text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      처리중...
                    </div>
                  ) : (
                    '무료 체험 신청하기'
                  )}
                </button>
                <p className="text-sm text-gray-500 mt-3">
                  신청 후 24시간 내에 담당자가 연락드립니다
                </p>
              </div>
            </form>
          </div>

          {/* 추가 안내 */}
          <div className="mt-8 text-center">
            <div className="inline-flex items-center px-4 py-2 bg-green-50 text-green-800 rounded-full text-sm">
              <CheckCircle className="w-4 h-4 mr-2" />
              평균 1시간 내 연락 • 즉시 체험 가능
            </div>
          </div>
        </div>
      </section>

      {/* 무료 체험 조건 섹션 */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                무료 체험 안내
              </h2>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-8 mb-8">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">포함되는 것</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center text-gray-700">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                        30일 무료 사용
                      </div>
                      <div className="flex items-center text-gray-700">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                        모든 프리미엄 기능
                      </div>
                      <div className="flex items-center text-gray-700">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                        무제한 A/B 테스트
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center text-gray-700">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                        1:1 전문가 지원
                      </div>
                      <div className="flex items-center text-gray-700">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                        실시간 데이터 분석
                      </div>
                      <div className="flex items-center text-gray-700">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                        맞춤 설정 지원
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">자주 묻는 질문</h3>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div key={index} className="border-b border-blue-100 last:border-b-0 pb-4 last:pb-0">
                    <h4 className="font-medium text-gray-900 mb-2">{faq.question}</h4>
                    <p className="text-gray-600 text-sm">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 최종 CTA 섹션 */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8">
            <Sparkles className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
            <h2 className="text-3xl font-bold mb-4">지금 바로 시작하세요!</h2>
            <p className="text-xl text-gray-300 mb-8">
              성공적인 A/B 테스트 경험이 여러분을 기다리고 있습니다
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center max-w-md mx-auto">
            <button 
              onClick={scrollToContactForm}
              className="w-full px-6 py-3 bg-white text-gray-900 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 font-medium"
            >
              무료로 시작하기
            </button>
            <span className="text-gray-400">또는</span>
            <button 
              onClick={openChat}
              className="w-full px-6 py-3 bg-transparent text-white border border-white/30 rounded-lg shadow-lg hover:shadow-xl hover:bg-white/10 transition-all duration-300 font-medium"
            >
              채팅 문의
            </button>
          </div>

          <div className="mt-8 flex items-center justify-center space-x-6 text-sm text-gray-400">
            <div className="flex items-center">
              <CheckCircle className="w-4 h-4 mr-1" />
              신용카드 불필요
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-4 h-4 mr-1" />
              즉시 시작 가능
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-4 h-4 mr-1" />
              전문가 지원
            </div>
          </div>
        </div>
      </section>

      {/* 애니메이션 스타일 */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-fade-in {
          animation: fade-in 0.3s ease-out forwards;
        }
        
        @media (prefers-reduced-motion) {
          .animate-fade-in {
            animation: none;
          }
        }
      `}</style>

      <Footer />
    </div>
  );
};

export default FreeStart;